import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import LpgCancelOutline from "@/icons/Lpg/CancelOutline";

import { Button } from "../Button";
import { Portal } from "../Portal";
import { A11yText } from "../A11yText";
import { Icon } from "../Icon";
import { useFocusTrap } from "../hooks/useFocusTrap";

import styles from "./ModalGallery.scss";

function ModalGalleryPortal({
  node,
  isOpened,
  isVisible: isVisibleProp,
  closeText,
  close,
  portalless,
  children,
  trigger,
  ...otherProps
}) {
  const [isVisible, setIsVisible] = useState(isVisibleProp);
  const wrapperRef = useRef();
  const modalRef = useRef();
  const { handleTabBehaviour } = useFocusTrap(modalRef);

  const triggerElement =
    trigger || (typeof document !== "undefined" && document.activeElement);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  function afterClose() {
    modalRef.current.removeEventListener("transitionend", afterClose);
    if (close) {
      close();
    }
  }

  function closePortal() {
    modalRef.current.addEventListener("transitionend", afterClose);
    setIsVisible(false);
  }

  function checkDocumentKeyDown(event) {
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      closePortal();
    } else if (event.key === "Tab" || event.keyCode === 9) {
      handleTabBehaviour(event);
    }
  }

  const checkOverlayClick = (event) => {
    if (!modalRef.current.contains(event.target)) {
      closePortal();
    }
  };

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
    if (wrapperRef.current) {
      wrapperRef.current.addEventListener("click", checkOverlayClick);
    }
    document.addEventListener("keydown", checkDocumentKeyDown);

    return () => {
      triggerElement.focus();

      if (wrapperRef.current) {
        wrapperRef.current.removeEventListener("click", checkOverlayClick);
      }
      document.removeEventListener("keydown", checkDocumentKeyDown);
    };
  }, []);

  const PortalTag = portalless ? "div" : Portal;

  return (
    <PortalTag node={node}>
      <div
        className={cx(styles.container, { "is-visible": isVisible })}
        ref={wrapperRef}
      >
        <div
          className={cx(styles.content, { "is-visible": isVisible })}
          ref={modalRef}
          aria-modal="true"
          role="dialog"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          {...otherProps}
        >
          <Button
            variants={[Button.Variant.clean, Button.Variant.reactangle]}
            type="button"
            tabIndex={0}
            onClick={closePortal}
            customClass={styles["close-button"]}
          >
            <Icon icon={LpgCancelOutline} size={Icon.Size.medium} />
            <A11yText>{closeText}</A11yText>
          </Button>
          <div className={styles.inner}>{children}</div>
        </div>
      </div>
    </PortalTag>
  );
}

ModalGalleryPortal.propTypes = {
  node:
    typeof Element !== "undefined"
      ? PropTypes.instanceOf(Element).isRequired
      : PropTypes.number,
  isOpened: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool,
  closeText: PropTypes.string.isRequired,
  close: PropTypes.func,
  portalless: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  trigger: PropTypes.instanceOf(
    typeof Element === "undefined" ? () => {} : Element
  ),
};

ModalGalleryPortal.defaultProps = {
  node: null,
  close: null,
  portalless: false,
  children: null,
  trigger: null,
  isVisible: false,
};

ModalGalleryPortal.displayName = "ModalGalleryPortal";

export default ModalGalleryPortal;
